import { useCallback, useEffect, useRef, useState } from "react";
import { getMe, logout as apiLogout, subscribeAuthExpired, type User } from "@/lib/api";
import { canRunTransientRetry, transientRetryDelayMs } from "@/lib/transient-retry";

type AuthState =
  | { status: "loading"; connectionUnavailable: boolean }
  | { status: "unauthenticated" }
  | { status: "authenticated"; user: User };

function getErrorStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const status = Reflect.get(error, "status");
  return typeof status === "number" && Number.isFinite(status) ? status : null;
}

export function resolveAuthCheckFailure(current: AuthState, error: unknown): AuthState {
  if (getErrorStatus(error) === 401) {
    return { status: "unauthenticated" };
  }
  if (current.status === "loading") {
    return { status: "loading", connectionUnavailable: true };
  }
  return current;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    connectionUnavailable: false,
  });
  const mountedRef = useRef(false);
  const retryTimerRef = useRef<number | null>(null);
  const retryAttemptRef = useRef(0);
  const checkGenerationRef = useRef(0);
  const checkInFlightRef = useRef<{
    generation: number;
    request: Promise<void>;
  } | null>(null);
  const checkAuthRef = useRef<() => Promise<void>>(async () => {});

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const checkAuth = useCallback((): Promise<void> => {
    clearRetryTimer();
    const inFlight = checkInFlightRef.current;
    if (inFlight?.generation === checkGenerationRef.current) {
      return inFlight.request;
    }

    const generation = ++checkGenerationRef.current;
    const request = (async () => {
      try {
        const user = await getMe({ suppressAuthExpired: true });
        if (!mountedRef.current || generation !== checkGenerationRef.current) {
          return;
        }
        retryAttemptRef.current = 0;
        setState({ status: "authenticated", user });
      } catch (error) {
        if (!mountedRef.current || generation !== checkGenerationRef.current) {
          return;
        }
        setState((current) => resolveAuthCheckFailure(current, error));
        if (getErrorStatus(error) === 401) {
          retryAttemptRef.current = 0;
          return;
        }
        const retryDelay = transientRetryDelayMs(retryAttemptRef.current);
        retryAttemptRef.current += 1;
        retryTimerRef.current = window.setTimeout(() => {
          retryTimerRef.current = null;
          if (!canRunTransientRetry(document.visibilityState, navigator.onLine !== false)) {
            return;
          }
          void checkAuthRef.current();
        }, retryDelay);
      }
    })();

    checkInFlightRef.current = { generation, request };
    void request.finally(() => {
      if (checkInFlightRef.current?.request === request) {
        checkInFlightRef.current = null;
      }
    });
    return request;
  }, [clearRetryTimer]);

  checkAuthRef.current = checkAuth;

  useEffect(() => {
    mountedRef.current = true;
    void checkAuth();
    const hasPendingAuthCheck = () =>
      retryAttemptRef.current > 0 || checkInFlightRef.current !== null;
    const handleOnline = () => {
      if (!hasPendingAuthCheck()) {
        return;
      }
      if (!canRunTransientRetry(document.visibilityState, true)) {
        return;
      }
      retryAttemptRef.current = 0;
      void checkAuth();
    };
    const handleVisibilityChange = () => {
      if (!hasPendingAuthCheck()) {
        return;
      }
      if (!canRunTransientRetry(document.visibilityState, navigator.onLine !== false)) {
        return;
      }
      void checkAuth();
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      mountedRef.current = false;
      checkGenerationRef.current += 1;
      clearRetryTimer();
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkAuth, clearRetryTimer]);

  useEffect(
    () =>
      subscribeAuthExpired(() => {
        void checkAuth();
      }),
    [checkAuth],
  );

  const logout = useCallback(async () => {
    checkGenerationRef.current += 1;
    retryAttemptRef.current = 0;
    clearRetryTimer();
    try {
      await apiLogout();
    } catch {
      /* ignore */
    }
    if (mountedRef.current) {
      setState({ status: "unauthenticated" });
    }
  }, [clearRetryTimer]);

  return { ...state, checkAuth, logout };
}
